import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle2, X } from 'lucide-react';

/**
 * AriBriefingCarousel — one briefing card at a time (owner-locked 2026-06).
 * Behaves like notifications: highest priority first, completed/dismissed
 * cards disappear and the next becomes #1. Empty deck → "Everything looks
 * good today." Swipe left/right or use Previous/Next.
 *
 * Card shape: { key, icon, iconBg, title, message, cta, onAction,
 *               dismissible, onDismiss, body (custom JSX for info panels) }
 * Styled for Ari's purple surface (white/10 card, white text) — shared by
 * the mobile briefing and the desktop Ari rail.
 */
const AriBriefingCarousel = ({ cards }) => {
  const [idx, setIdx] = useState(0);
  const touchRef = useRef(null);

  useEffect(() => {
    if (idx > cards.length - 1) setIdx(Math.max(0, cards.length - 1));
  }, [cards.length, idx]);

  if (!cards.length) {
    return (
      <div className="rounded-xl bg-white/10 backdrop-blur-sm p-4 text-center" data-testid="ari-briefing-empty">
        <CheckCircle2 className="w-6 h-6 text-emerald-300 mx-auto mb-1.5" />
        <p className="text-[13px] text-white/90 font-medium">Everything looks good today.</p>
      </div>
    );
  }

  const card = cards[Math.min(idx, cards.length - 1)];

  const onTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchRef.current == null) return;
    const dx = e.changedTouches[0].clientX - touchRef.current;
    touchRef.current = null;
    if (dx < -50) setIdx((i) => Math.min(cards.length - 1, i + 1));
    else if (dx > 50) setIdx((i) => Math.max(0, i - 1));
  };

  return (
    <div data-testid="ari-briefing-carousel" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="rounded-xl bg-white/10 backdrop-blur-sm p-3" data-testid={`ari-briefing-card-${card.key}`}>
        {card.body ? card.body : (
          <div className="flex items-start gap-2.5">
            {card.icon && (
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${card.iconBg || 'bg-white/20'}`}>
                {card.icon}
              </div>
            )}
            <div className="flex-1 min-w-0">
              {card.title && <p className="text-[13px] font-bold text-white mb-0.5" data-testid="ari-card-title">{card.title}</p>}
              <p className="text-[12px] text-white/90 leading-snug" data-testid="ari-card-message">{card.message}</p>
              {card.cta && (
                <button
                  onClick={card.onAction}
                  className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/20 hover:bg-white/30 text-[11px] font-bold text-white transition-colors"
                  data-testid={`ari-card-cta-${card.key}`}
                >
                  {card.cta}
                </button>
              )}
            </div>
            {card.dismissible && (
              <button
                onClick={card.onDismiss}
                className="shrink-0 -mr-0.5 mt-0.5 text-white/60 hover:text-white"
                data-testid={`ari-card-dismiss-${card.key}`}
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-2 px-1">
        <button
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0}
          className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${idx === 0 ? 'text-white/25' : 'text-white/70 hover:text-white'}`}
          data-testid="ari-carousel-prev"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Previous
        </button>
        <span className="text-[10px] text-white/60 font-semibold" data-testid="ari-carousel-counter">
          {Math.min(idx, cards.length - 1) + 1} of {cards.length}
        </span>
        <button
          onClick={() => setIdx((i) => Math.min(cards.length - 1, i + 1))}
          disabled={idx >= cards.length - 1}
          className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${idx >= cards.length - 1 ? 'text-white/25' : 'text-white/70 hover:text-white'}`}
          data-testid="ari-carousel-next"
        >
          Next <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default AriBriefingCarousel;
